import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// --------------------------------------------------------------------------- //
// Shared dark-theme tokens (kept in sync with AdminDashboard)
// --------------------------------------------------------------------------- //
export const PANEL_BG = "#0F1424";
export const INNER_BG = "#0A0F1E";
export const inputCls =
  "bg-[#0A0F1E] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#1E6BFF]/40";

export const slugify = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// --------------------------------------------------------------------------- //
// Status badge for Active / Inactive state
// --------------------------------------------------------------------------- //
export const ActiveBadge = ({ active }) =>
  active ? (
    <Badge className="border bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30">
      Active
    </Badge>
  ) : (
    <Badge className="border bg-white/5 text-white/50 border-white/15">
      Inactive
    </Badge>
  );

// --------------------------------------------------------------------------- //
// Reusable form fields (labelled, dark-themed)
// --------------------------------------------------------------------------- //
export const Field = ({ label, hint, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && (
      <Label className="text-white/70 text-xs font-medium">{label}</Label>
    )}
    {children}
    {hint && <p className="text-white/35 text-[11px] leading-snug">{hint}</p>}
  </div>
);

export const TextField = ({ label, hint, value, onChange, testid, ...rest }) => (
  <Field label={label} hint={hint}>
    <Input
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} h-10`}
      {...rest}
    />
  </Field>
);

export const NumberField = ({ label, hint, value, onChange, testid, ...rest }) => (
  <Field label={label} hint={hint}>
    <Input
      type="number"
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value === "" ? "" : Number(e.target.value))
      }
      className={`${inputCls} h-10 tabular-nums`}
      {...rest}
    />
  </Field>
);

export const TextareaField = ({ label, hint, value, onChange, testid, rows = 3 }) => (
  <Field label={label} hint={hint}>
    <Textarea
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className={`${inputCls} min-h-[70px]`}
    />
  </Field>
);

// Array field: one item per line, stored/returned as string[]
export const ArrayField = ({ label, hint, value, onChange, testid, rows = 3 }) => (
  <Field
    label={label}
    hint={hint || "One item per line."}
  >
    <Textarea
      data-testid={testid}
      value={Array.isArray(value) ? value.join("\n") : value ?? ""}
      onChange={(e) =>
        onChange(
          e.target.value
            .split("\n")
            .map((x) => x.trim())
            .filter(Boolean)
        )
      }
      rows={rows}
      className={`${inputCls} min-h-[70px]`}
    />
  </Field>
);

export const SwitchField = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0A0F1E] px-3 py-2.5">
    <span className="text-white/70 text-sm">{label}</span>
    <Switch
      data-testid={testid}
      checked={!!checked}
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#2EE59D]"
    />
  </div>
);

// --------------------------------------------------------------------------- //
// Destructive confirmation dialog
// --------------------------------------------------------------------------- //
export const DeleteConfirm = ({ open, onOpenChange, title, description, onConfirm, busy, testid }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent
      className="bg-[#0F1424] border-white/10 text-white"
      data-testid={testid}
    >
      <AlertDialogHeader>
        <AlertDialogTitle className="text-white">{title}</AlertDialogTitle>
        <AlertDialogDescription className="text-white/55">
          {description}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white">
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          data-testid="rims-delete-confirm-button"
          disabled={busy}
          onClick={(e) => {
            e.preventDefault();
            onConfirm();
          }}
          className="bg-[#FF5C7A] hover:bg-[#ff456a] text-white border-0"
        >
          Delete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
